import React from "react";
import { Colors } from "../constants/colors";

function PrivacyPolicy() {
  return (
    <div style={styles.page}>
      <div style={styles.content}>
        <h1 style={styles.centerText}>Privacy Policy</h1>
        <p style={styles.centerText}>Last Updated: November 01, 2025</p>

        <p>
          Thank you for using <strong>Naryi</strong>! Naryi helps college students manage screen time, improve focus habits, and make the most of their day while keeping privacy a top priority.
        </p>

        <ul style={styles.list}>
          <li>
            <strong>Apple Screen Time API:</strong> On iOS, we use Apple’s Screen Time API to help monitor and manage app usage. We do not store this data; it remains on your device.
          </li>
          <li>
            <strong>Data stays on your device:</strong> Information such as app usage or focus statistics is only used on your device and not stored on our servers unless you opt-in.
          </li>
          <li>
            <strong>Minimal data collection:</strong> We collect only what is necessary to provide the Service. You control optional data like schedules or task lists.
          </li>
          <li>
            <strong>Privacy is our priority:</strong> We never collect or store unnecessary personal information and never sell user data to advertisers.
          </li>
          <li>
            <strong>Subscription support:</strong> Naryi is free with optional subscription plans to support development. Users are never treated as the product.
          </li>
        </ul>

        <h2>1. Introduction</h2>
        <p>
          <strong>1.1</strong> Protecting your personal information is important. Contact us at <a href="mailto:hello@naryi.com" style={styles.link}>hello@naryi.com</a>.
        </p>
        <p>
          <strong>1.2</strong> Naryi (“we,” “us,” or “our”) is a mobile app for college students to track screen time, manage schedules, and improve focus. This Privacy Policy explains how and why we collect, use, and share your information.
        </p>
        <p>
          <strong>1.3</strong> You can sign up for free or premium accounts. Both are collectively called “Naryi,” “App,” or “Service.”
        </p>
        <p>
          <strong>1.4</strong> Reading this Privacy Policy helps you understand your rights. Contact <a href="mailto:support@naryi.com" style={styles.link}>support@naryi.com</a> with questions.
        </p>

        <h2>2. Consent</h2>
        <p><strong>2.1</strong> By using Naryi, you consent to the collection, use, and storage of your data as described in this policy.</p>
        <p><strong>2.2</strong> You can withdraw consent at any time by contacting us. Some features may be limited if consent is withdrawn.</p>

        <h2>3. Minors and Age Restrictions</h2>
        <p><strong>3.1</strong> Naryi does not knowingly collect data from children under 13. If we discover such data, it will be deleted immediately.</p>
        <p><strong>3.2</strong> Users under 18 require parental consent. By using Naryi, you confirm you meet the age requirement or have permission.</p>

        <h2>4. Information Collection</h2>
        <p>We collect information in three main ways:</p>
        <h3>(a) Personal Information</h3>
        <ul style={styles.list}>
          <li><strong>4a.1</strong> Information you provide, such as name, email, and account credentials.</li>
          <li><strong>4a.2</strong> Optional student-related info like schedules, classes, tasks, or club activities to enhance the Service.</li>
          <li><strong>4a.3</strong> Device permissions, including calendar, camera, or push notifications, which you can control.</li>
        </ul>
        <h3>(b) Automatically Collected Information</h3>
        <ul style={styles.list}>
          <li><strong>4b.1</strong> Device and usage data: IP address, device type, browser, OS, app activity, and analytics.</li>
          <li><strong>4b.2</strong> Crash reports and error logs to maintain performance and troubleshoot issues.</li>
        </ul>
        <h3>(c) Third-Party Services</h3>
        <ul style={styles.list}>
          <li><strong>4c.1</strong> Integration with Google APIs, Apple services, or cloud storage for optional features.</li>
          <li><strong>4c.2</strong> Third-party services may collect additional data according to their policies. We minimize and control this sharing.</li>
        </ul>
        <h3>(d) Cookies and Tracking</h3>
        <ul style={styles.list}>
          <li><strong>4d.1</strong> We may use cookies or similar technologies to improve app performance and store preferences.</li>
          <li><strong>4d.2</strong> Cookies do not contain personally identifiable information unless you provide it directly.</li>
        </ul>

        <h2>5. Information Processing</h2>
        <ul style={styles.list}>
          <li>Manage your account and authentication.</li>
          <li>Deliver requested features and app functionality.</li>
          <li>Send notifications, reminders, or service updates.</li>
          <li>Respond to support requests and inquiries.</li>
          <li>Analyze app usage to improve services and understand trends.</li>
          <li>Enhance and personalize your experience using optional data like schedules, classes, and usage statistics.</li>
          <li>Communicate optional promotional content.</li>
          <li>Support app development, maintenance, and security through anonymized analytics.</li>
        </ul>

        <h2>6. Safety and Security</h2>
        <p><strong>6.1</strong> We use industry-standard technical and organizational measures to protect data.</p>
        <p><strong>6.2</strong> No system is completely secure; users should keep account credentials private.</p>

        <h2>7. Sharing Your Information</h2>
        <ul style={styles.list}>
          <li>With your consent for specific features.</li>
          <li>With service providers necessary to operate the app.</li>
          <li>As legally required or to protect rights and safety.</li>
          <li>During mergers, acquisitions, or asset transfers.</li>
        </ul>

        <h2>8. International Transfers</h2>
        <p>Your data may be processed or stored outside your country. We ensure safeguards such as Standard Contractual Clauses or Binding Corporate Rules where required.</p>

        <h2>9. Retention of Your Information</h2>
        <p>We keep data only as long as necessary to provide services, comply with legal obligations, or resolve disputes. When no longer needed, data is securely deleted or anonymized.</p>

        <h2>10. Security of Your Information</h2>
        <p>We continuously monitor systems and implement technical and organizational safeguards. However, absolute security cannot be guaranteed.</p>

        <h2>11. Your Privacy Rights</h2>
        <p>Depending on your region, you may have rights including access, correction, deletion, restriction of processing, data portability, objection to automated decisions, or withdrawing consent. Contact <a href="mailto:support@naryi.com" style={styles.link}>support@naryi.com</a> to exercise these rights.</p>

        <h2>12. Other Regions’ Privacy Rights</h2>
        <p>Users in Australia, New Zealand, South Africa, and similar jurisdictions may request access, correction, or deletion of personal information and submit complaints to privacy authorities.</p>

        <h2>13. Analytics and Performance Monitoring</h2>
        <p>We may collect anonymous analytics to understand app usage trends, improve functionality, and enhance user experience. Personal data is never shared without consent.</p>

        <h2>14. Updates to This Privacy Policy</h2>
        <p>We may update this Privacy Policy to comply with law or improve transparency. Material changes are communicated via the “Last Updated” date or direct notice when appropriate.</p>

        <h2>15. Reviewing, Updating, or Deleting Your Information</h2>
        <p>You may request access, correction, deletion, or withdrawal of consent via <a href="http://www.naryi.com/contact-us" style={styles.link}>http://www.naryi.com/contact-us</a>. We will respond in accordance with applicable laws. Some rights may be limited by law.</p>
      </div>
    </div>
  );
}

const styles = {
  page: {
    padding: "3rem 1.5rem",
    fontFamily: "system-ui, sans-serif",
    lineHeight: "1.6",
    color: "#333",
    display: "flex",
    backgroundColor: Colors.background.light,
    justifyContent: "center",
  },
  content: {
    maxWidth: "700px",
    textAlign: "left",
  },
  centerText: {
    textAlign: "center",
  },
  list: {
    textAlign: "left",
    margin: "1rem 0",
    paddingLeft: "1.5rem",
  },
  link: {
    color: "#000",       // Black links
    textDecoration: "underline", // optional
  },
};

export default PrivacyPolicy;
